<template>
  <section class="tools-index">
    <header class="tools-index__header">
      <h1>Tools for AI agents working with beads</h1>
      <p class="tools-index__subline">
        Endpoints and helpers external LLM agents can call directly. Each tool is paste-friendly,
        self-describing, and works without auth.
      </p>
    </header>

    <ul class="tools-index__list">
      <li
        v-for="tool in tools"
        :key="tool.slug"
        class="tools-index__card"
      >
        <router-link :to="tool.path" class="tools-index__card-link">
          <h2 class="tools-index__card-title">{{ tool.title }}</h2>
          <p class="tools-index__card-summary">{{ tool.summary }}</p>
          <div class="tools-index__card-meta">
            <span class="console-badge console-badge--neutral console-badge--small">{{ tool.kind }}</span>
            <span class="tools-index__card-cta">Read &rarr;</span>
          </div>
        </router-link>
      </li>
    </ul>
  </section>
</template>

<script setup>
const tools = [
  {
    slug: 'job-description-prompt',
    path: '/console/tools/job-description-prompt',
    title: 'Job-description prompt',
    kind: 'Public HTTP API',
    summary: 'Paste-ready prompt that teaches an external LLM agent how to author a beads-conformant GitHub issue or JobSubmission payload, covering the nine job archetypes the planner already models.',
  },
  {
    slug: 'standards',
    path: '/console/tools/standards',
    title: 'Standards',
    kind: 'MCP server + HTTP API',
    summary: 'Searchable library of coding standards and design patterns. One-liner installer registers an MCP server for Claude Code, Copilot, Cursor, Amazon Q, and Codex; every tool is also reachable as plain HTTP.',
  },
  {
    slug: 'knowledge-base',
    path: '/console/tools/knowledge-base',
    title: 'Knowledge base',
    kind: 'Public HTTP API',
    summary: 'RAG search across curated articles and research notes. POST a query, get a synthesized answer plus the underlying sources, ready to drop into an agent’s context.',
  },
]
</script>

<style scoped>
.tools-index__header {
  margin-bottom: 28px;
}

.tools-index__header h1 {
  margin: 0 0 8px;
  font-size: 1.6rem;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.tools-index__subline {
  margin: 0;
  color: var(--shell-text-muted);
  line-height: 1.7;
  max-width: 70ch;
}

.tools-index__list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.tools-index__card {
  border: 1px solid var(--shell-border);
  border-radius: 4px;
  background: var(--shell-surface);
  transition: border-color 0.1s, box-shadow 0.1s;
}

.tools-index__card:hover {
  border-color: var(--shell-text-muted);
}

.tools-index__card-link {
  display: block;
  padding: 18px 20px;
  color: inherit;
  text-decoration: none;
}

.tools-index__card-title {
  margin: 0 0 6px;
  font-size: 1.05rem;
  font-weight: 600;
}

.tools-index__card-summary {
  margin: 0 0 16px;
  color: var(--shell-text-muted);
  line-height: 1.6;
  font-size: 0.92rem;
}

.tools-index__card-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.tools-index__card-cta {
  font-family: var(--shell-mono-font);
  font-size: 0.78rem;
  color: var(--shell-text);
}
</style>
